import { Loader, Stack, Text } from "@mantine/core";
import { IconArrowLeft, IconCancel, IconCheck } from "@tabler/icons-react";
import { useSuspenseQuery } from "@tanstack/react-query";
import { createFileRoute, Link } from "@tanstack/react-router";
import { Suspense, useEffect, useState } from "react";
import { httpPostGraphql } from "shared/api/httpClient";
import { useGraphqlMutation } from "shared/hooks/useGraphql";
import { notify } from "shared/utils/notify";
import { graphql } from "src/gql";
import { PermissionSchema } from "src/gql/zod";
import { z } from "zod";

const roleModifyParamSchema = z.object({
    role: z.string(),
    add: z.string().array(),
    remove: z.string().array(),
});

const Page = () => {
    const { role: roleName, add, remove } = Route.useSearch();
    const [error, setError] = useState<string>();

    const {
        data: { roles },
    } = useSuspenseQuery({
        queryKey: ["roles"],
        queryFn: () =>
            httpPostGraphql(
                graphql(`
                    query RoleOMaticModifyRoles {
                        roles(offset: 0, limit: 1000) {
                            id
                            name
                            permissions {
                                id
                            }
                        }
                    }
                `),
                {},
            ),
    });

    const mutation = useGraphqlMutation({
        document: graphql(`
            mutation RoleOMaticModifyRole($roleId: UUID!, $permissions: [Permission!]!) {
                updateRolePermissions(params: { roleId: $roleId, permissions: $permissions }) {
                    id
                }
            }
        `),
    });

    useEffect(() => {
        const role = roles.find((r) => r.name == roleName);
        if (!role) {
            setError("Matching role not found");
            return;
        }
        const newPerms = role.permissions
            .map((it) => it.id)
            .filter((it) => !remove.includes(it))
            .concat(PermissionSchema.parse(add));

        mutation.mutateAsync({ roleId: role.id, permissions: newPerms }).catch(notify.catch);
    }, []);

    return (
        <Stack h="100%" justify="center" align="center">
            {mutation.isPending ? (
                <Loader size="xl" />
            ) : (
                <>
                    {mutation.isError || !!error ? (
                        <>
                            <IconCancel />
                            <Text>An error has occurred:</Text>
                            <Text c="red">{error?.toString() ?? mutation.error?.message}</Text>
                        </>
                    ) : (
                        <>
                            <IconCheck />
                            <Text>Success!</Text>
                        </>
                    )}
                    <Link
                        to="/devtools/role-o-matic"
                        style={{
                            display: "flex",
                            alignItems: "center",
                            color: "var(--mantine-color-anchor)",
                            padding: "var(--mantine-spacing-xs)",
                            border: "1px solid var(--mantine-color-anchor)",
                            borderRadius: "var(--mantine-radius-lg)",
                        }}
                    >
                        <IconArrowLeft size={16} />
                        Back to Role-o-Matic
                    </Link>
                </>
            )}
        </Stack>
    );
};

/** @public */
export const Route = createFileRoute("/_auth/_dev/devtools/role-o-matic/modify")({
    component: () => (
        <Suspense fallback={<Loader />}>
            <Page />
        </Suspense>
    ),
    validateSearch: roleModifyParamSchema,
});
